// Renderer-Registry pro Canvas-Objekt-Typ. Ersetzt den Switch in ObjectLayer:ObjectView.
//
// Konvention (siehe docs/canvas-types.md):
//   - Compiler-Forcing-Function: `satisfies RendererMap` — neue ObjectType-Variante
//     muss hier ein renderInner liefern, sonst Build-Bruch.
//   - Outer-Wrapper (canvas-obj-class, Pointer-Handlers, Position, Ports, Resize-Handles)
//     bleibt in ObjectLayer zentral. Renderer liefern nur den Type-spezifischen Inhalt
//     + optionale data-/aria-Attribute + optionale Inline-Style-Erweiterung.
//   - Editor-Render wird als Callback injiziert, damit Inline-Edit zentral kontrolliert
//     bleibt (EditableText, commitEdit-Pfad, Cancel-Logik).

import { type JSX, useEffect, useState } from "react";
import { ensureAssetUrl } from "./asset-url-cache.js";
import { useBoardId } from "./board-context.js";
import { type LucideComp, ensureIcon, getIcon } from "./icon-cache.js";
import type {
  BlockObj,
  CanvasObject,
  DependencyObj,
  FrameObj,
  IconObj,
  ImageObj,
  MindmapNodeObj,
  ObjectType,
  PainObj,
  RegionObj,
  RequirementObj,
  ShapeObj,
  ShapeRectObj,
  StickyObj,
  TextObj,
} from "./objects/types.js";
import {
  BPMN_DEFAULT_ACCENT,
  bpmnMarkerPaths,
  bpmnStrokeWidth,
  shapePath,
} from "./shape-renderer.js";

export interface RenderInnerProps<T extends CanvasObject> {
  readonly obj: T;
  readonly editing: boolean;
  readonly editor: (initial: string) => JSX.Element;
}

export interface ObjectRendererSpec<T extends CanvasObject> {
  /** Inner content (Badges, Labels, Editor-Mount). Wrapper liefert ObjectLayer. */
  readonly renderInner: (props: RenderInnerProps<T>) => JSX.Element;
  /** Type-spezifische data-/aria-Attribute am Outer-Wrapper. */
  readonly outerAttrs?: (obj: T) => Record<string, string | undefined>;
  /** Type-spezifische Inline-Style-Erweiterung am Outer-Wrapper (z.B. borderColor). */
  readonly outerStyle?: (obj: T) => React.CSSProperties;
}

type RendererMap = {
  readonly [K in ObjectType]: ObjectRendererSpec<Extract<CanvasObject, { type: K }>>;
};

const stickyRenderer: ObjectRendererSpec<StickyObj> = {
  renderInner: ({ obj, editing, editor }) => (editing ? editor(obj.text) : <>{obj.text}</>),
  outerAttrs: (obj) => ({ "data-color": obj.color ?? "yellow" }),
};

const textRenderer: ObjectRendererSpec<TextObj> = {
  renderInner: ({ obj, editing, editor }) => (editing ? editor(obj.text) : <>{obj.text}</>),
};

const shapeRectRenderer: ObjectRendererSpec<ShapeRectObj> = {
  renderInner: ({ obj, editing, editor }) =>
    editing ? editor(obj.label) : <span className="canvas-shape-label">{obj.label}</span>,
};

const REQ_CATEGORY_SHORT: Record<string, string> = {
  functional: "F",
  "non-functional": "NF",
  data: "D",
  "out-of-scope": "OoS",
};

const requirementRenderer: ObjectRendererSpec<RequirementObj> = {
  outerAttrs: (obj) => ({ "data-priority": obj.priority, "data-category": obj.category }),
  renderInner: ({ obj, editing, editor }) => (
    <>
      <span className="canvas-semantic-badge">{obj.priority.toUpperCase()}</span>
      <span className="canvas-category-badge" data-category={obj.category} title={obj.category}>
        {REQ_CATEGORY_SHORT[obj.category] ?? obj.category}
      </span>
      {editing ? editor(obj.text) : <span className="canvas-semantic-text">{obj.text}</span>}
    </>
  ),
};

const painRenderer: ObjectRendererSpec<PainObj> = {
  outerAttrs: (obj) => ({ "data-severity": obj.severity }),
  renderInner: ({ obj, editing, editor }) => (
    <>
      <span className="canvas-semantic-badge">{obj.severity.toUpperCase()}</span>
      {editing ? editor(obj.text) : <span className="canvas-semantic-text">{obj.text}</span>}
    </>
  ),
};

const dependencyRenderer: ObjectRendererSpec<DependencyObj> = {
  outerAttrs: (obj) => ({
    "data-kind": obj.kind,
    "data-status": obj.status,
    ...(obj.severity ? { "data-severity": obj.severity } : {}),
  }),
  renderInner: ({ obj, editing, editor }) => (
    <>
      <span className="canvas-semantic-badge">{obj.kind}</span>
      {obj.severity && <span className="canvas-semantic-badge">{obj.severity.toUpperCase()}</span>}
      {editing ? editor(obj.text) : <span className="canvas-semantic-text">{obj.text}</span>}
      <span className="canvas-semantic-status" data-status={obj.status}>
        {obj.status}
      </span>
    </>
  ),
};

const blockRenderer: ObjectRendererSpec<BlockObj> = {
  outerAttrs: (obj) => ({ "data-method-id": obj.methodId, "aria-label": obj.label }),
  renderInner: ({ obj, editing, editor }) =>
    editing ? (
      <span className="canvas-block-label canvas-block-label-editing">{editor(obj.label)}</span>
    ) : (
      <span className="canvas-block-label">{obj.label}</span>
    ),
};

const regionRenderer: ObjectRendererSpec<RegionObj> = {
  outerAttrs: (obj) => ({ "data-block-id": obj.blockId, "data-accepts": obj.accepts }),
  outerStyle: (obj) => (obj.accent ? { borderColor: obj.accent } : {}),
  renderInner: ({ obj, editing, editor }) =>
    editing ? (
      <span className="canvas-region-label canvas-region-label-editing">{editor(obj.label)}</span>
    ) : (
      <span className="canvas-region-label">{obj.label}</span>
    ),
};

const frameRenderer: ObjectRendererSpec<FrameObj> = {
  outerStyle: (obj) => (obj.accent ? { borderColor: obj.accent } : {}),
  renderInner: ({ obj, editing, editor }) =>
    editing ? (
      <span className="canvas-frame-title canvas-frame-title-editing">{editor(obj.title)}</span>
    ) : (
      <span className="canvas-frame-title">{obj.title}</span>
    ),
};

const mindmapNodeRenderer: ObjectRendererSpec<MindmapNodeObj> = {
  outerAttrs: (obj) => ({ "data-has-parent": obj.parentNodeId ? "true" : "false" }),
  // Wenn User einen accent gesetzt hat, Pastell-Background + dunklen Text fix setzen
  // (sonst gewinnt die Level-Color-Regel mit weissem Text bei Root und der Pastell
  // wird unleserlich).
  outerStyle: (obj) => (obj.accent ? { background: obj.accent, color: "#1f2937" } : {}),
  renderInner: ({ obj, editing, editor }) =>
    editing ? (
      <span className="canvas-mindmap-text canvas-mindmap-text-editing">{editor(obj.text)}</span>
    ) : (
      <span className="canvas-mindmap-text">{obj.text}</span>
    ),
};

function IconBoxed({ obj }: { obj: IconObj }): JSX.Element {
  const [Cmp, setCmp] = useState<LucideComp | undefined>(() => getIcon(obj.iconName));
  useEffect(() => {
    const direct = getIcon(obj.iconName);
    if (direct) {
      setCmp(() => direct);
      return;
    }
    let cancelled = false;
    void ensureIcon(obj.iconName).then((resolved) => {
      if (!cancelled) setCmp(() => resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [obj.iconName]);
  if (!Cmp) {
    return <span className="canvas-icon-fallback">{obj.iconName}</span>;
  }
  const size = Math.min(obj.width, obj.height);
  return <Cmp size={size} color={obj.color ?? "#1f2937"} absoluteStrokeWidth />;
}

const iconRenderer: ObjectRendererSpec<IconObj> = {
  outerAttrs: (obj) => ({ "data-icon-name": obj.iconName }),
  renderInner: ({ obj }) => <IconBoxed obj={obj} />,
};

function ImageBoxed({ obj }: { obj: ImageObj }): JSX.Element {
  const boardId = useBoardId();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!boardId) return;
    let cancelled = false;
    setFailed(false);
    void ensureAssetUrl(boardId, obj.assetId)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [boardId, obj.assetId]);
  if (failed) return <span className="canvas-image-fallback">⚠</span>;
  if (!url) return <span className="canvas-image-loading" aria-hidden="true" />;
  return <img className="canvas-image" src={url} alt={obj.originalName ?? ""} draggable={false} />;
}

const imageRenderer: ObjectRendererSpec<ImageObj> = {
  outerAttrs: (obj) => ({ "data-asset-id": obj.assetId }),
  renderInner: ({ obj }) => <ImageBoxed obj={obj} />,
};

const shapeRenderer: ObjectRendererSpec<ShapeObj> = {
  outerAttrs: (obj) => ({
    "data-shape-kind": obj.kind,
    ...(obj.semantics ? { "data-shape-semantics": obj.semantics } : {}),
  }),
  // outerStyle leer lassen — SVG fuellt sich selbst
  renderInner: ({ obj, editing, editor }) => {
    const d = shapePath(obj.kind, obj.width, obj.height);
    const sem = obj.semantics;
    // BPMN-Shapes: weisser Fill + farbiger Rand (Ring-Optik), Strichstaerke + Marker je Typ.
    // Plain-Shapes: Accent als Fill, neutraler Rand (Welle-1-Verhalten unveraendert).
    const accent = obj.accent ?? (sem ? BPMN_DEFAULT_ACCENT[sem] : undefined) ?? "#f1f5f9";
    const fill = sem ? "#ffffff" : accent;
    const stroke = sem ? accent : "#475569";
    const strokeWidth = sem ? bpmnStrokeWidth(sem) : 1.4;
    const markers = sem ? bpmnMarkerPaths(sem, obj.width, obj.height) : [];
    return (
      <>
        <svg
          className="canvas-shape-svg"
          viewBox={`0 0 ${obj.width} ${obj.height}`}
          width={obj.width}
          height={obj.height}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d={d} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
          {markers.map((md) => (
            <path key={md} d={md} fill="none" stroke={stroke} strokeWidth={1.4} />
          ))}
        </svg>
        <span className="canvas-shape-label">{editing ? editor(obj.text) : obj.text}</span>
      </>
    );
  },
};

export const OBJECT_RENDERERS = {
  sticky: stickyRenderer,
  text: textRenderer,
  "shape-rect": shapeRectRenderer,
  requirement: requirementRenderer,
  pain: painRenderer,
  dependency: dependencyRenderer,
  block: blockRenderer,
  region: regionRenderer,
  frame: frameRenderer,
  "mindmap-node": mindmapNodeRenderer,
  shape: shapeRenderer,
  icon: iconRenderer,
  image: imageRenderer,
} as const satisfies RendererMap;

/** Runtime-Lookup. Cast auf widened-Spec ist unvermeidbar wegen TS-Invarianz in Renderer-Generic. */
export function getObjectRenderer(obj: CanvasObject): ObjectRendererSpec<CanvasObject> {
  return OBJECT_RENDERERS[obj.type] as unknown as ObjectRendererSpec<CanvasObject>;
}
